/**
 * Session breakdown — turns a package's redemption ledger into the three
 * states a customer actually recognises: attended, upcoming, available.
 *
 * WHY THIS EXISTS
 * ---------------
 * `package_usage_logs` is a REDEMPTION ledger, not an attendance ledger. A
 * credit leaves `remaining_booking_count` the moment a booking is created
 * (kit.recalculate_remaining_credits recomputes from the logs on INSERT), not
 * when the session happens. Fixed-slot packages are the extreme case:
 * purchase_fixed_slot_package writes one deduction row per generated session
 * AT PURCHASE, so the package sits at 0 remaining before the customer has
 * attended anything.
 *
 * Rendering `original - remaining` as "used" therefore tells a customer with
 * four future sessions that they have used all four. This module splits that
 * number by the session's own date instead.
 *
 * PURE — no server imports, no i18n, no formatting. Both the single-package
 * card and the bundle card consume it, and it is unit-tested directly.
 */

/** The subset of a usage-log row this module needs. */
export interface SessionLogInput {
  /** Positive = deduction, negative = restoration (cancellation refund). */
  credits_used: number;
  /** The session's own date (YYYY-MM-DD). NULL when the booking is gone. */
  booking_date: string | null;
  /**
   * The session's start time (HH:MM[:SS]). Optional — only the renewal
   * deadline needs it, and not every caller selects it.
   */
  start_time?: string | null;
  /** When the credit was spent. Fallback ordering key. */
  used_at: string;
  /**
   * Booking lifecycle. 'cancelled' rows must not count as sessions.
   * Optional: the loader may not select it, in which case netting by
   * booking_id is the only cancellation signal available.
   */
  booking_status?: string | null;
  /** Soft-delete flag; a deleted booking is not a session. */
  is_deleted?: boolean | null;
  /** Identity used to net a refund against its original deduction. */
  booking_id?: string | null;
  participant_id?: string | null;
  /** Free-text provenance. Diagnostic only — never branched on (see
   *  isBooklessAdjustment). */
  notes?: string | null;
}

export interface SessionBreakdown {
  /** Sessions whose date has passed. */
  attended: number;
  /** Sessions booked for a future date — the ones misread as "used" today. */
  upcoming: number;
  /** Credits not yet spent on any booking (`remaining_booking_count`). */
  available: number;
  extraAvailable: number;
  /** attended + upcoming + available, including net credits granted. */
  total: number;
  /** The next future session's date, ISO (YYYY-MM-DD). NULL when none. */
  nextSessionDate: string | null;
  /**
   * True when the ledger could not fully explain the spent credits — the
   * caller truncated logs, or a booking row was hard-deleted (the FK is
   * ON DELETE SET NULL, which orphans the log). Callers should fall back to
   * the plain remaining/original display rather than show wrong buckets.
   */
  isPartial: boolean;
}

/**
 * Nets refunds against deductions so a cancelled session counts as neither
 * attended nor upcoming.
 *
 * Cancellation writes a compensating `credits_used: -1` row rather than
 * deleting the original — court bookings via
 * packages/features/calendar/server/bookings/cancel-booking-action.ts, classes
 * via cancel-class-participant.actions.ts — so a cancelled
 * booking normally leaves BOTH rows in the ledger. Counting only positive
 * rows would therefore double-count it: the credit is already back in
 * `remaining_booking_count`, and the session never happened.
 *
 * Grouping key is the booking (or class participant) the credit was spent on,
 * which is what the +1 and -1 rows share.
 */
function netLogsByBooking(logs: SessionLogInput[]) {
  const byKey = new Map<
    string,
    { net: number; log: SessionLogInput }
  >();
  // Rows with no identity can't be netted; keep them as standalone entries so
  // their credits still register.
  const unkeyed: Array<{ net: number; log: SessionLogInput }> = [];

  for (const log of logs) {
    const key = log.booking_id ?? log.participant_id ?? null;

    if (!key) {
      unkeyed.push({ net: log.credits_used, log });
      continue;
    }

    const existing = byKey.get(key);

    if (existing) {
      existing.net += log.credits_used;
      // Keep whichever row carries a session date — the refund row may not.
      if (!existing.log.booking_date && log.booking_date) {
        existing.log = log;
      }
    } else {
      byKey.set(key, { net: log.credits_used, log });
    }
  }

  return [...byKey.values(), ...unkeyed];
}

/** A booking that was cancelled or soft-deleted is not a session. */
function isLiveBooking(log: SessionLogInput) {
  if (log.is_deleted === true) return false;
  if (log.booking_status === 'cancelled') return false;
  return true;
}

/**
 * A positive log row that never referenced a booking is a BOOK-LESS
 * adjustment: credits consumed with no session to date them by. Three real
 * sources, all verified against production:
 *   - package import — one aggregate row, `import:` note
 *     (package-import-actions.ts)
 *   - manual admin credit adjustments ("Manual adjustment by admin: -N credits")
 *   - legacy migration notes from the tenant's previous system
 *
 * These credits are genuinely spent, so they count as attended. Keying on the
 * ABSENCE of booking_id rather than on note text keeps this robust: notes are
 * free-text and tenant-authored, so matching them would be brittle, and any
 * future book-less adjustment source is handled automatically.
 *
 * Distinct from an ORPHANED row, where booking_id was set but the booking was
 * hard-deleted (the FK is ON DELETE SET NULL). We cannot tell those apart
 * after the fact — both arrive as booking_id null — but production has zero
 * orphans, and treating a spent credit as attended is the safer reading
 * either way.
 */
function isBooklessAdjustment(log: SessionLogInput) {
  return !log.booking_id && !log.participant_id;
}

/**
 * Splits a package's spent credits into attended vs upcoming.
 *
 * @param logs   Usage-log rows for ONE package. May include refund rows.
 * @param remainingCount `remaining_booking_count` — credits not yet booked.
 * @param now    Injected for deterministic tests.
 */
export function computeSessionBreakdown(
  logs: SessionLogInput[],
  remainingCount: number,
  originalCount: number,
  now: Date = new Date(),
): SessionBreakdown {
  const available = Math.max(0, remainingCount);

  let attended = 0;
  let upcoming = 0;
  let undated = 0;
  let booklessCreditsUsed = 0;
  let nextSessionDate: string | null = null;

  // Compare on the session's own calendar day: a class earlier today has
  // happened, and a date-only string must not be read as UTC midnight (which
  // would make "today" look future-dated for tenants ahead of UTC).
  const todayKey = toDayKey(now);

  for (const { net, log } of netLogsByBooking(logs)) {
    if (!log.booking_date && isBooklessAdjustment(log)) {
      booklessCreditsUsed += net;
      continue;
    }

    // net <= 0: fully refunded (cancelled) or a stray restoration row.
    if (net <= 0) continue;
    if (!isLiveBooking(log)) continue;

    if (!log.booking_date) {
      // A booking was referenced but carries no date — the row is unusable.
      undated += net;
      continue;
    }

    if (log.booking_date <= todayKey) {
      attended += net;
    } else {
      upcoming += net;

      if (!nextSessionDate || log.booking_date < nextSessionDate) {
        nextSessionDate = log.booking_date;
      }
    }
  }

  attended += Math.max(0, booklessCreditsUsed);

  const netCreditsGranted = Math.max(0, -booklessCreditsUsed);
  const extraAvailable = Math.min(available, netCreditsGranted);
  const spent = originalCount + netCreditsGranted - available;

  // The ledger must place every spent credit on one side of the split. It
  // fails to when logs were truncated (attended + upcoming falls short of
  // spent) or when a booking was hard-deleted — the FK is ON DELETE SET NULL,
  // so the credit is real but has no session date to bucket it by. Both cases
  // would render misleading counts, so flag them and let the caller fall back
  // to the plain remaining/original display.
  const isPartial = undated > 0 || attended + upcoming !== spent;

  return {
    attended,
    upcoming,
    available,
    extraAvailable,
    total: attended + upcoming + available,
    nextSessionDate,
    isPartial,
  };
}

/**
 * Local calendar day as YYYY-MM-DD, so it compares directly against
 * `court_bookings.booking_date` (a date, not a timestamp) without any
 * timezone shift from `toISOString()`.
 */
function toDayKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Sums breakdowns across the rows of a bundle. Bundle parents carry no credits
 * of their own, so callers pass the siblings.
 */
export function mergeSessionBreakdowns(
  breakdowns: SessionBreakdown[],
): SessionBreakdown {
  return breakdowns.reduce<SessionBreakdown>(
    (acc, b) => ({
      attended: acc.attended + b.attended,
      upcoming: acc.upcoming + b.upcoming,
      available: acc.available + b.available,
      extraAvailable: acc.extraAvailable + b.extraAvailable,
      total: acc.total + b.total,
      nextSessionDate:
        !acc.nextSessionDate ||
        (b.nextSessionDate && b.nextSessionDate < acc.nextSessionDate)
          ? (b.nextSessionDate ?? acc.nextSessionDate)
          : acc.nextSessionDate,
      isPartial: acc.isPartial || b.isPartial,
    }),
    {
      attended: 0,
      upcoming: 0,
      available: 0,
      extraAvailable: 0,
      total: 0,
      nextSessionDate: null,
      isPartial: false,
    },
  );
}


/**
 * Renewal deadline — a fixed-slot package stays renewable up until its LAST
 * session STARTS, and closes from that moment on.
 *
 * WHY THIS EXISTS
 * ---------------
 * Renewal secures the same recurring slot for the next cycle before it opens
 * to the public, so the customer needs the whole run of the package to decide.
 * The window must not, however, stay open right to the package's expiry: a
 * package "with hours left" was still offering Renew, which sells a renewal
 * against a cycle that has effectively already ended.
 *
 * The last session's START is the cut-off: once the customer is sitting in
 * their final session, the cycle is over for renewal purposes.
 *
 * WHY NOT fixed_slot.{last_session_date, start_time}
 * --------------------------------------------------
 * Those two fields do not describe the same session. The RPC builds
 * `last_session_date` as MAX(booking_date) but `start_time` as the FIRST
 * session's time (ORDER BY booking_date ASC)[1] — see fixed_slot_meta in
 * 31-family-members B.sql. For a 2-day package whose weekdays run at
 * different times, pairing them yields a deadline that belongs to no real
 * session. The usage-log ledger carries each session's own date AND time, so
 * the last session is derived from it here.
 *
 * FAILS OPEN. If no dated session can be identified (a partial/truncated
 * ledger, or logs without times), this returns true: wrongly hiding Renew
 * strands a customer who genuinely wants to keep their slot, which is worse
 * than showing it slightly late.
 *
 * CLIENT-SIDE ONLY — this compares against the BROWSER's clock, matching the
 * rest of this module and the cards that call it. A customer travelling
 * outside the venue's timezone can therefore see Renew shift by their UTC
 * offset. That is cosmetic: createFixedSlotPackageRenewalCheckoutAction
 * re-applies the same deadline on the TENANT's clock before any charge, so
 * the authoritative answer never depends on where the customer is standing.
 */
export function isWithinRenewalWindow(
  logs: SessionLogInput[],
  now: Date = new Date(),
): boolean {
  const lastSessionStart = findLastSessionStart(logs);

  if (!lastSessionStart) return true;

  return now.getTime() < lastSessionStart.getTime();
}

/**
 * The start instant of the latest live session in the ledger, or NULL when
 * none can be dated. Sessions cancelled or refunded (net <= 0) are excluded —
 * a cancelled final session must not close the renewal window.
 *
 * Times are read as LOCAL, matching how a venue's booking times are stored
 * (a wall-clock time, not an instant) and how the rest of this module
 * compares dates.
 */
function findLastSessionStart(logs: SessionLogInput[]): Date | null {
  let latest: Date | null = null;

  for (const { net, log } of netLogsByBooking(logs)) {
    if (net <= 0) continue;
    if (!isLiveBooking(log)) continue;
    if (!log.booking_date) continue;

    const start = parseLocalDateTime(log.booking_date, log.start_time);

    if (!latest || start.getTime() > latest.getTime()) {
      latest = start;
    }
  }

  return latest;
}

/**
 * Combines a YYYY-MM-DD date with an optional HH:MM[:SS] time into a LOCAL
 * Date. A missing time falls back to end-of-day, which keeps the window open
 * for that session rather than closing it at midnight on unknown evidence.
 */
function parseLocalDateTime(day: string, time: string | null | undefined) {
  const [year, month, date] = day.split('-').map(Number);
  const [hours, minutes] = (time ?? '23:59').split(':').map(Number);

  return new Date(
    year ?? 0,
    (month ?? 1) - 1,
    date ?? 1,
    hours ?? 23,
    minutes ?? 59,
    0,
    0,
  );
}
